import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Clock, Users, Send, Globe, Calendar, Phone, Mail, User, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const PublicBooking = () => {
  const { slug } = useParams();
  const [affiliate, setAffiliate] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    activity_id: '', activity_name: '', guest_name: '', guest_phone: '', guest_email: '',
    booking_date: '', booking_time: '', num_guests: 1, pickup_location: '', additional_notes: '', stay_type: ''
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [affRes, actRes] = await Promise.all([
          axios.get(`${API}/public/affiliate/${slug}`),
          axios.get(`${API}/public/affiliate/${slug}/activities`)
        ]);
        setAffiliate(affRes.data);
        setActivities(actRes.data);
      } catch {
        setNotFound(true);
      } finally { setLoading(false); }
    };
    load();
  }, [slug]);

  useEffect(() => {
    if (affiliate) {
      document.title = `${affiliate.company_name} - Activities | Sefer Tunisia`;
    }
  }, [affiliate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/affiliate/${slug}/book`, form);
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit booking');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
      <div className="text-lg text-gray-500">Loading...</div>
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
      <div className="text-center">
        <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-700">Page Not Found</h1>
        <p className="text-gray-500 mt-2">This affiliate link is not valid.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="booking-success">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Send className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Submitted!</h2>
        <p className="text-gray-600 mb-6">Your booking request has been sent. You will be contacted shortly to confirm.</p>
        <button onClick={() => { setSubmitted(false); setForm({ activity_id: '', activity_name: '', guest_name: '', guest_phone: '', guest_email: '', booking_date: '', booking_time: '', num_guests: 1, pickup_location: '', additional_notes: '', stay_type: '' }); }}
          className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition">
          Book Another Activity
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="public-booking-page">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-emerald-700">Sefer Tunisia Activities</h1>
            <p className="text-sm text-gray-500">by {affiliate?.company_name}</p>
          </div>
          <Globe className="w-8 h-8 text-emerald-600" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Activities Grid */}
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Available Activities</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {activities.map(a => (
            <button key={a.id}
              onClick={() => setForm(f => ({...f, activity_id: a.id, activity_name: a.name}))}
              className={`text-left p-4 rounded-xl border-2 transition
                ${form.activity_id === a.id ? 'border-emerald-500 bg-emerald-50 shadow-md' : 'border-gray-200 bg-white hover:border-emerald-300'}`}
              data-testid={`public-activity-${a.id}`}>
              <div className="font-semibold text-gray-900">{a.name}</div>
              {a.description && <p className="text-xs text-gray-500 mt-1">{a.description}</p>}
              <div className="mt-2 text-emerald-600 font-bold">{a.price} {a.currency}</div>
              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs
                ${a.type === 'trip' ? 'bg-blue-100 text-blue-700' : a.type === 'service' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {a.type}
              </span>
            </button>
          ))}
        </div>

        {/* Booking Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Book an Activity</h2>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="public-booking-form">
            {!form.activity_id && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
                Please select an activity above first.
              </div>
            )}
            {form.activity_id && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700 font-medium">
                Selected: {form.activity_name}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4" /> Full Name *
                </label>
                <input required value={form.guest_name} onChange={e => setForm(f => ({...f, guest_name: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="John Doe"
                  data-testid="public-guest-name" />
              </div>
              <div>
                <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                  <Phone className="w-4 h-4" /> WhatsApp / Phone *
                </label>
                <input required value={form.guest_phone} onChange={e => setForm(f => ({...f, guest_phone: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="+216..."
                  data-testid="public-guest-phone" />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                <Mail className="w-4 h-4" /> Email (optional)
              </label>
              <input type="email" value={form.guest_email} onChange={e => setForm(f => ({...f, guest_email: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="email@example.com" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4" /> Preferred Date *
                </label>
                <input type="date" required value={form.booking_date} onChange={e => setForm(f => ({...f, booking_date: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="public-booking-date" />
              </div>
              <div>
                <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4" /> Preferred Time
                </label>
                <input type="time" value={form.booking_time} onChange={e => setForm(f => ({...f, booking_time: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                  <Users className="w-4 h-4" /> Number of Guests *
                </label>
                <input type="number" min="1" required value={form.num_guests}
                  onChange={e => setForm(f => ({...f, num_guests: parseInt(e.target.value) || 1}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="public-num-guests" />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                <MapPin className="w-4 h-4" /> Pickup / Meeting Location
              </label>
              <input value={form.pickup_location} onChange={e => setForm(f => ({...f, pickup_location: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Hotel name, address..." />
            </div>

            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                <FileText className="w-4 h-4" /> Additional Notes
              </label>
              <textarea value={form.additional_notes} onChange={e => setForm(f => ({...f, additional_notes: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" rows={3} placeholder="Any special requests..." />
            </div>

            <button type="submit" disabled={submitting || !form.activity_id}
              className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-lg"
              data-testid="public-submit-booking">
              {submitting ? 'Submitting...' : 'Submit Booking Request'}
            </button>

            <p className="text-xs text-gray-400 text-center">
              No online payment required. You will be contacted to confirm your booking.
            </p>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t mt-12 py-6 text-center text-sm text-gray-500">
        Powered by <span className="font-semibold text-emerald-600">Sefer Tunisia</span>
      </div>
    </div>
  );
};

export default PublicBooking;
